import type { Component } from 'vue';
import { DocumentRevisionStatus } from '@/shared/lib';
import { Stage } from '@/shared/lib';
import type { Pagination, SearchParams } from '@/shared/lib';

import {
  PHYSICAL_LOCATION_SORT_FIELDS,
  PHYSICAL_LOCATION_SORT_ORDERS,
} from '../config/physical-locations';

export interface DocumentRevisionNotification {
  notificationId: number;
  dates: string[];
  members: {
    id: number;
    user: { id: number; name: string; status?: string | null; deletedAt?: string | null };
    positions: { id: number; name: string }[];
  }[];
}

// Quality Record enums
export enum QualityRecordStatus {
  DISPONIVEL = 'DISPONIVEL',
  EM_USO = 'EM_USO',
  ARQUIVADO = 'ARQUIVADO',
}

export enum QualityRecordStage {
  EM_ANDAMENTO = 'EM_ANDAMENTO',
  CONCLUIDO = 'CONCLUIDO',
  AGUARDANDO_IP = 'AGUARDANDO_IP',
  AGUARDANDO_AE = 'AGUARDANDO_AE',
}

import type { User } from '@/entities/User/@x/Document';

import type { Branch } from '@/entities/Branch/@x/Document';

// PhysicalLocation type
export interface PhysicalLocation {
  id: number;
  name: string;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
  documentRevisions?: DocumentRevision[];
}

export interface PhysicalLocationApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export type PhysicalLocationSortField = (typeof PHYSICAL_LOCATION_SORT_FIELDS)[number];
export type PhysicalLocationSortOrder = (typeof PHYSICAL_LOCATION_SORT_ORDERS)[number];

export interface PhysicalLocationPaginatedApiResponse<T> {
  success: boolean;
  data: T;
  metadata: Pagination;
  message?: string;
}

export interface PhysicalLocationPageParams {
  page: number;
  itemsPerPage: number;
  term?: string;
  sort?: PhysicalLocationSortField;
  order?: PhysicalLocationSortOrder;
}

export interface PhysicalLocationPageResponse {
  items: PhysicalLocation[];
  metadata: Pagination;
}

export interface CreatePhysicalLocationsPayload {
  names: string[];
}

export interface UpdatePhysicalLocationPayload {
  names: string[];
}

export interface UpdatePhysicalLocationResponse {
  updatedLocation: PhysicalLocation;
  createdLocations: PhysicalLocation[];
}

export type PhysicalLocationListResponse = PhysicalLocationApiResponse<PhysicalLocation[]>;

export type PhysicalLocationPageApiResponse = PhysicalLocationPaginatedApiResponse<
  PhysicalLocation[]
>;

// Approval types
export interface Approval {
  id: number;
  justification?: string;
  status: string;
  userId: number;
  documentRevisionId: number;
  trainingId?: number;
  document?: Document;
  user?: User;
  Training?: Training[];
}

// Training types
export interface Training {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  notes: string;
  documentApprovalId: number;
  userId: number;
  positions: PositionTraining[];
  sessions: TrainingSession[];
  documentApproval: Approval;
  documentVerifications: DocumentVerification[];
  User: User;
  users: UserTraining[];
}

export interface TrainingInfo {
  required: boolean;
  justification?: string | null;
}

interface PositionTraining {
  id: number;
  positionId: number;
  trainingId: number;
}

interface TrainingSession {
  id: number;
  trainingId: number;
  trainingDate: string;
}

interface DocumentVerification {
  id: number;
  documentId: number;
  verificationDate: Date;
}

interface UserTraining {
  id: number;
  userId: number;
  trainingId: number;
}

// Document types
export interface DocumentRevision {
  id: number;
  createdAt: string;
  updatedAt: string;
  originalFileName?: string | null;
  fileSize?: string | number | null;
  approvalDate: string;
  approvedAt: string;
  approvalManagerId?: number | null;
  approval?: {
    id: number;
    approvedBy: {
      name: string;
      registrationNumber?: string | null;
    };
    approvedAt: string | null;
  };
  revision: number;
  validityDate: string;
  effectiveDate: string;
  hasTraining?: boolean;
  status: DocumentRevisionStatus;
  externalRevision?: string | null;
  stage: Stage | null;
  positions?: {
    id: number;
    name: string;
  }[];
  training?: {
    programmed: boolean;
    trainingJustification: string | null;
  };
  approvedBy?: {
    name: string;
    registrationNumber?: string | null;
    position?: {
      name: string;
    };
  };
  documentId: number;
  docUrl: string | null;
  document: Document;
  origin?: DocumentOrigin;
  changelogEntries: ChangeLogEntry[];
  trainingDetails?: {
    notes?: string | null;
    sessions?: {
      id: number;
      trainingId: number;
      trainingDate: string;
      responsibleUserId: number | null;
      responsibleUserName: string | null;
      responsibleMemberId?: number | null;
      responsibleUserPositionId?: number | null;
    }[];
    members?: {
      id: number;
      memberId: number;
      trainingId: number;
      user: {
        id: number;
        name: string;
        email: string;
      };
      position: {
        id: number;
        name: string;
      } | null;
    }[];
  };
  physicalLocations?: Array<{
    id: number;
    name: string;
    distribution: {
      quantity: number | null;
    };
  }>;
  /**
   * Distinct modules this revision is linked to in Diretórios, already sorted by
   * name on the backend. Two revisions of the same document may carry different
   * sets. Not to be confused with the legacy single `module` of the Excel export.
   */
  directoryModules?: Array<{
    id: number;
    name: string;
  }>;
  qualityRecordStatus?: QualityRecordStatus;
  qualityRecordStage?: QualityRecordStage;
  currentUser?: string;
  config?: DocumentRevisionConfig;
  notifications?: DocumentRevisionNotification | null;
  hasPreviousRevisions?: boolean;
  previousRevisionsCount?: number;
}

export interface DocumentHistoryData {
  requestDate?: string | Date;
  revisions: DocumentRevision[];
}

export interface Document {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  prefix: string;
  docPath?: string;
  requestDate: string;
  documentIdentity?: {
    type: string;
  };
  type: string;
  revision?: number;
  userId?: number;
  memberId?: number;
  user?: User;
  createdBy?: {
    name: string;
  };
  url?: string;
  branch?: Branch;
  branchId?: number;
  revisions?: DocumentRevision[];
  documentApprovals?: Approval[];
}

export interface ChangeLogEntry {
  id: number;
  createdAt: string;
  updatedAt: string;
  description: string;
  documentRevisionId: number;
}

export interface DocumentPrefix {
  id: number;
  code: string;
  description: string;
}

export interface TipoDocumento {
  id: number;
  name: string;
  checked: boolean;
}

export interface DocumentTableProps {
  documents: Document[];
}

// Document Control types
export interface UserPosition {
  name: string;
}

export interface UserWithPositions {
  name: string;
  positions: UserPosition[];
}

export interface DocumentElaboration {
  elaborator: UserWithPositions;
  elaboratedAt: string | null;
}

export interface DocumentApproval {
  approver: UserWithPositions;
  approvedAt: string | null;
  memberId?: number;
  positionId?: number | null;
}

export interface DocumentVerificationData {
  verifier: UserWithPositions;
  verifiedAt: string | null;
  memberId?: number | null;
  positionId?: number | null;
}

export interface DocumentControlTrainingMinistrante {
  sessionId: number;
  trainingDate: string;
  userId: number | null;
  userName: string | null;
  positionId: number | null;
  positionName: string | null;
  positions: {
    id: number;
    name: string;
  }[];
}

export interface DocumentControlTrainingData {
  hasTraining: boolean;
  trainingId?: number;
  notes?: string | null;
  contentCreatedAt?: string | null;
  contentResponsible?: {
    userId: number;
    userName: string;
    positionId?: number | null;
    positions: {
      id: number;
      name: string;
    }[];
  } | null;
  ministrantes?: DocumentControlTrainingMinistrante[];
  positions: {
    id: number;
    name: string;
  }[];
  members: {
    id: number;
    memberId: number;
    user: {
      id: number;
      name: string;
      email: string;
      memberships: {
        position: {
          id: number;
          name: string;
        };
      }[];
    };
  }[];
  sessions: {
    id: number;
    trainingDate: string;
    responsibleMemberId: number | null;
    responsibleUserPositionId: number | null;
  }[];
}

export interface DocumentControlResponse {
  elaboration: DocumentElaboration;
  approval:
    | (DocumentApproval & { justification?: string | null; trainingJustification?: string | null })
    | null;
  verification: DocumentVerificationData | null;
  effectiveDate?: string | null;
  validityDate?: string | null;
  previousRevisionEffectiveDate?: string | null;
  verificationDate?: string | null;
  revisionNumber?: number;
  documentPrefix?: string;
  documentTitle?: string;
  training?: DocumentControlTrainingData | null;
}

export interface RevisionTrainingApiSession {
  id: number;
  trainingDate: string | Date;
  responsibleUserId?: number | null;
  responsibleUser?: { id: number; name: string; email?: string } | null;
  responsibleUserPosition?: { id: number; name: string } | null;
}

export interface RevisionTrainingApiTrainingUser {
  id: number;
  name: string;
  email?: string;
  memberships?: { position?: { id: number; name: string } }[];
}

export interface RevisionTrainingApiTraining {
  id: number;
  notes?: string | null;
  type?: string;
  sessions?: RevisionTrainingApiSession[];
  users?: RevisionTrainingApiTrainingUser[];
}

export interface RevisionTrainingApiApproval {
  approver?: { name?: string; position?: { name?: string } };
  createdAt?: string;
  justification?: string | null;
}

export interface RevisionTrainingApiResponse {
  hasTraining?: boolean;
  training?: RevisionTrainingApiTraining | null;
  approvals?: RevisionTrainingApiApproval[];
}

export interface RevisionTrainingSessionDisplayRow {
  id: number;
  dataLabel: string;
  ministrante: string;
  cargo: string;
}

// Validity History types
export interface ValidityHistoryItem {
  id: number;
  revision: number;
  documentName: string;
  prefix: string;
  validityDate: string | null;
  effectiveDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  elaboration: {
    name: string;
    position: string;
    date: string;
  };
  approval?: {
    name: string;
    position: string;
    date: string;
  };
  verification?: {
    name: string;
    position: string;
    date: string;
  };
}

// Master List types
export interface MasterListDocument {
  prefix: string;
  documentName: string | null;
  revision: string;
  externalRevision?: string | null;
  validityDate: string | null;
  status: string;
  format: string;
}

export interface MasterListResponse {
  success: boolean;
  data: {
    code: string;
    data: MasterListDocument[];
  };
  message: string;
}

// Filtered List types
export interface FilteredListParams {
  type: 'document' | 'module';
  id: string;
  name: string;
}

export interface FilteredListState {
  loading: boolean;
  documents: DocumentRevision[];
  metadata: {
    page: number;
    total: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    sort: string;
    order: string;
    [key: string]: string | number | boolean | null | undefined;
  };
}

export interface FilteredListConfig {
  allowMultipleSelection: boolean;
  showCheckboxes: boolean;
  showIcons: boolean;
  maxDepth: number;
  autoExpandFirstLevel: boolean;
}

export type FilterOptions = import('./filterTypes').FiltersFormData;

export interface DateRange {
  start: string;
  end: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PaginationOptions {
  page: number;
  itemsPerPage: number;
}

export enum FilteredListStatus {
  VIGENTE = 'vigente',
  OBSOLETO = 'obsoleto',
  ARQUIVADO = 'arquivado',
  INATIVO = 'inativo',
  REVOGADO = 'revogado',
}

export interface SelectOption {
  id: string | number;
  name: string;
}

export enum DocumentFormat {
  EL = 'EL',
  EL_FS = 'EL_FS',
}

export enum DocumentOrigin {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
}

export enum DocumentAccessPermission {
  VIEW_ONLY = 'VIEW_ONLY',
  VIEW_AND_PRINT = 'VIEW_AND_PRINT',
}

export interface DocumentRevisionConfig {
  format: DocumentFormat;
  origin: DocumentOrigin;
  includeInMasterList?: boolean;
  accessPermission?: DocumentAccessPermission;
  editedByUser?: { id: number; name: string } | null;
  editedByPosition?: { id: number; name: string } | null;
  configuredAt?: string | null;
}

export enum DocumentType {
  INTERNO = 'interno',
  EXTERNO = 'externo',
}

// Document Store types
export interface DocumentRevisionsStoreState {
  revisions: DocumentRevision[];
  allRevisions: DocumentRevision[];
  metadata: Pagination | undefined;
  loading: boolean;
  loadingMore: boolean;
  hasMoreRevisions: boolean;
  currentPage: number;
  selectedQuantity: number;
  searchParams: SearchParams | null;
  lastSearchKey: string;
}

export interface DocumentRevisionsStoreGetters {
  hasResults: boolean;
  isInfiniteScrollMode: boolean;
  shouldShowTable: boolean;
}

export interface DocumentRevisionsStoreActions {
  performSearch: (params: SearchParams, page?: number, append?: boolean) => Promise<void>;
  loadMoreRevisions: () => Promise<void>;
  setQuantity: (quantity: number) => void;
  removeRevisions: (deletedIds: number[]) => void;
  clearCache: () => void;
  refreshCurrentSearch: () => Promise<void>;
}

export interface DocumentRevisionsStore
  extends
    DocumentRevisionsStoreState,
    DocumentRevisionsStoreGetters,
    DocumentRevisionsStoreActions {}

export interface DocumentRevisionsComponentProps {
  orderOptions?: Component;
  tableComponent: Component;
  onPageChanged?: (page: number) => void;
  isFilterActive?: boolean;
}

export interface DocumentRevisionsComponentEvents {
  'update:sort': (sortCamp: string, orderFlow: string) => void;
  'update:filter': (filters: Record<string, any>) => void;
  'filter-clicked': () => void;
  'page-changed': (page: number) => void;
  delete: (id: number | number[]) => void;
  'documents-deleted': (deletedIds: number[]) => void;
}

export interface SearchConfiguration {
  batchSize: number;
  minLoadInterval: number;
  defaultQuantity: number;
  infiniteScrollTriggerMargin: string;
}

export interface SearchCacheKey {
  search: string;
  sort: { sort?: string; order?: string };
  filter: Record<string, any>;
  amount: number;
  searchUrl: string;
  context?: 'search' | 'gestao';
}

export interface SearchResultMetadata extends Pagination {
  searchKey: string;
  timestamp: number;
  totalResults: number;
}

export interface InfiniteScrollConfig {
  rootMargin: string;
  threshold: number;
  minBatchSize: number;
  loadThrottle: number;
}

export interface DocumentListUIState {
  shouldShowTable: boolean;
  shouldShowQuantitySelect: boolean;
  shouldShowNoResults: boolean;
  shouldShowEmptyPage: boolean;
  isUsingFilters: boolean;
  isUsingOrder: boolean;
  hasResults: boolean;
}
